import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import mergeClassNames from '../../lib/style-helpers/merge-class-name';
import { InputStateStyleProps } from '../../../../types/input-state-style-props';
import useFocus from '../../../../hooks/useFocus/useFocus';

export interface FocusBorderProps extends React.ComponentPropsWithoutRef<'span'> {
  height: string;
  className?: string;
}

export interface TextInputProps extends React.ComponentPropsWithoutRef<'input'> {
  name: string;
  className?: string;
  value?: string;
  handleChange: (value: string) => void;
  focusBorderProps?: FocusBorderProps;
  noBorder?: boolean;
}

function BaseTextInput({
  name,
  className,
  value,
  handleChange = () => {
    // Default no-op function for handleChange
  },
  noBorder = false,
  focusBorderProps = {
    height: '2px',
  },
  maxLength,
  ...props
}: TextInputProps) {
  const [inputValue, setInputValue] = useState(value);

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    hasFocus, onFocus, onBlur,
  } = useFocus(inputRef);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    handleChange(e.target.value);
  };

  return (
    <Container>
      <UnstyledInput
        {...props}
        name={name}
        ref={inputRef}
        className={mergeClassNames('UnstyledTextInput', `${hasFocus && '--focused'}`, className)}
        type={'text'}
        value={inputValue}
        onChange={onChange}
        focused={hasFocus}
        onFocus={onFocus}
        onBlur={onBlur}
        maxLength={maxLength}
      />
      {
        !noBorder && (
          <FocusBorder
            {...focusBorderProps}
            className={mergeClassNames('FocusBorder', `${hasFocus && '--focused'}`, focusBorderProps.className)}
          />
        )
      }
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const FocusBorder = styled.span < InputStateStyleProps > `
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 3px;
  background-color: ${({ theme }) => theme.colors.primary};
  transition: 100ms;
  &.--focused {
    width: 100%;
    transition: 150ms;
    left: 0;
  }
`;

const UnstyledInput = styled.input < InputStateStyleProps > `
  appearance: none;
  border: none;
  outline: none;
  width: 100%;
`;

export default BaseTextInput;
